import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Share, MoreVertical, Home } from 'lucide-react';

const INSTALL_PROMPT_KEY = 'wwi_install_prompt_dismissed';
const INSTALL_PROMPT_SHOWN_KEY = 'wwi_install_prompt_shown';
const SESSION_COUNT_KEY = 'wwi_session_count';
const TASK_COMPLETION_COUNT_KEY = 'wwi_completed_count';

// Custom event names for triggering the prompt
export const INSTALL_PROMPT_EVENTS = {
  TASK_COMPLETED: 'wwi_first_task_completed',
  RESET_USED: 'wwi_reset_used',
  CHECK_INSTALL_PROMPT: 'wwi_check_install_prompt',
};

/**
 * Track a task completion for install prompt trigger
 * Call this when a task is marked as done
 */
export const trackTaskCompletion = () => {
  try {
    const current = parseInt(localStorage.getItem(TASK_COMPLETION_COUNT_KEY) || '0', 10);
    localStorage.setItem(TASK_COMPLETION_COUNT_KEY, String(current + 1));
    // Dispatch event to check if prompt should show
    window.dispatchEvent(new CustomEvent(INSTALL_PROMPT_EVENTS.CHECK_INSTALL_PROMPT));
  } catch (e) {
    console.error('Error tracking task completion:', e);
  }
};

// Helper function to trigger install prompt from other components
export const triggerInstallPrompt = (eventType) => {
  window.dispatchEvent(new CustomEvent(eventType));
};

/**
 * InstallPrompt - Gentle PWA install prompt
 * 
 * NEW TRIGGER CONDITIONS (both must be met):
 * 1. User has completed ≥3 tasks (felt value)
 * 2. User has returned for a 2nd session (behavior repeat)
 * 
 * This ensures we only prompt users who have:
 * - Experienced enough value to want the app
 * - Shown commitment by coming back
 */
const InstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [platform, setPlatform] = useState(null); // 'ios', 'android', or null
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  // Get current session and completion counts
  const getCompletedCount = useCallback(() => {
    try {
      return parseInt(localStorage.getItem(TASK_COMPLETION_COUNT_KEY) || '0', 10);
    } catch {
      return 0;
    }
  }, []);

  const getSessionCount = useCallback(() => {
    try {
      return parseInt(localStorage.getItem(SESSION_COUNT_KEY) || '0', 10);
    } catch {
      return 0;
    }
  }, []);

  const shouldShowPrompt = useCallback(() => {
    // Check if already dismissed or shown
    const dismissed = localStorage.getItem(INSTALL_PROMPT_KEY);
    const alreadyShown = localStorage.getItem(INSTALL_PROMPT_SHOWN_KEY);
    if (dismissed || alreadyShown) return false;

    // Check if already installed as PWA
    if (window.matchMedia('(display-mode: standalone)').matches) return false;
    if (window.navigator.standalone === true) return false;

    // NEW: Both conditions must be met
    // ≥3 tasks completed AND ≥2 sessions
    const completedCount = getCompletedCount();
    const sessionCount = getSessionCount();
    
    return completedCount >= 3 && sessionCount >= 2;
  }, [getCompletedCount, getSessionCount]);

  // Track session on mount
  useEffect(() => {
    const lastSession = sessionStorage.getItem('wwi_current_session');
    if (!lastSession) {
      // This is a new session - increment counter
      try {
        const currentCount = parseInt(localStorage.getItem(SESSION_COUNT_KEY) || '0', 10);
        localStorage.setItem(SESSION_COUNT_KEY, String(currentCount + 1));
        sessionStorage.setItem('wwi_current_session', 'active');
      } catch (e) {
        console.error('Error tracking session:', e);
      }
    }
  }, []);

  useEffect(() => {
    // Detect platform
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    const isMobile = isIOS || isAndroid;

    // Only show on mobile
    if (!isMobile) return;

    setPlatform(isIOS ? 'ios' : 'android');

    // Listen for beforeinstallprompt (Android Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check if prompt should show (called after task completions)
    const checkAndShowPrompt = () => {
      if (!shouldShowPrompt()) return;
      
      // Delay showing by 1.5s to let completion animation finish
      setTimeout(() => {
        if (shouldShowPrompt()) {
          setShowPrompt(true);
          localStorage.setItem(INSTALL_PROMPT_SHOWN_KEY, 'true');
        }
      }, 1500);
    };

    // Listen for task completion events
    const handleTaskCompleted = () => {
      checkAndShowPrompt();
    };

    // Listen for explicit check events
    const handleCheckPrompt = () => {
      checkAndShowPrompt();
    };

    window.addEventListener(INSTALL_PROMPT_EVENTS.TASK_COMPLETED, handleTaskCompleted);
    window.addEventListener(INSTALL_PROMPT_EVENTS.CHECK_INSTALL_PROMPT, handleCheckPrompt);

    // Also check on mount (in case conditions already met from previous sessions)
    setTimeout(() => {
      if (shouldShowPrompt()) {
        setShowPrompt(true);
        localStorage.setItem(INSTALL_PROMPT_SHOWN_KEY, 'true');
      }
    }, 3000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener(INSTALL_PROMPT_EVENTS.TASK_COMPLETED, handleTaskCompleted);
      window.removeEventListener(INSTALL_PROMPT_EVENTS.CHECK_INSTALL_PROMPT, handleCheckPrompt);
    };
  }, [shouldShowPrompt]);

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem(INSTALL_PROMPT_KEY, 'true');
  };

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Android Chrome native install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        localStorage.setItem(INSTALL_PROMPT_KEY, 'true');
      }
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  if (!showPrompt || !platform) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-40"
        data-testid="install-prompt"
      >
        <div 
          className="bg-white rounded-2xl p-4 relative"
          style={{
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06)',
          }}
        >
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute -top-2 -right-2 p-1.5 bg-white rounded-full text-stone-400 hover:text-stone-600 hover:bg-stone-50 transition-colors"
            style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            }}
            aria-label="Dismiss"
            data-testid="install-prompt-dismiss"
          >
            <X size={14} />
          </button>

          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className="p-2.5 bg-[#FEF3E8] rounded-xl flex-shrink-0">
              <Home size={20} className="text-[#F58227]" />
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-stone-800 mb-0.5">
                One tap. Right where you left off.
              </p>
              <p className="text-xs text-stone-500 mb-2">
                Add to home screen — opens to your last task.
              </p>

              {platform === 'ios' ? (
                <p className="text-xs text-stone-400 leading-relaxed">
                  Tap <Share size={11} className="inline-block mx-0.5 -mt-0.5" /> then "Add to Home Screen"
                </p>
              ) : (
                <>
                  {deferredPrompt ? (
                    <button
                      onClick={handleInstallClick}
                      className="mt-1 px-4 py-2 bg-[#F58227] hover:bg-[#D96D19] text-white text-xs font-medium rounded-lg transition-colors"
                      data-testid="install-prompt-button"
                    >
                      Add to Home Screen
                    </button>
                  ) : (
                    <p className="text-xs text-stone-400 leading-relaxed">
                      Tap <MoreVertical size={11} className="inline-block mx-0.5 -mt-0.5" /> then "Install App"
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InstallPrompt;
